import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AlertBanner, Button, Card, Group, Stack, Tag, Text } from '@grovus/design-system'
import type { Intent } from '../auth/entry'
import type { Reconciled } from '../auth/reconcile'
import { acceptInvitation, declineInvitation, type Invitation } from '../data/members'
import { MEMBERS_COPY, type Lang } from '../i18n'
import { fmt } from '../lib/format'
import type { Invitations } from './useInvitations'

const DAY_MS = 86_400_000

interface Props {
  lang: Lang
  items: Invitation[]
  /** `shell` from the Invitations page; `entry` from No client access and the invite page. */
  origin: 'shell' | 'entry'
  /** The invite page's own invitation, drawn with the brand border. */
  highlight?: string | null
  onReconcile: (intent?: Intent) => Promise<Reconciled>
  invitations: Pick<Invitations, 'refetch' | 'settle'>
  /** The invite page clears its token as soon as Accept is sent. */
  onBeforeAccept?: () => void
  /** After a Decline that settled. */
  onDeclined?: () => void
  /** Shown instead of nothing when no card, kept card or Retry is left. */
  fallback?: ReactNode
  /** The way out, inside the highlighted card once its line is terminal. */
  leave?: ReactNode
}

type Joining = { invitation: Invitation; phase: 'pending' | 'failed' }

/**
 * One Card per received invitation, with Accept and Decline (GRO-106 design, "Invitations
 * list"). The accept write and the reads that show its result fail separately, so Accept
 * enters `joining`, which keeps the company from the card and never the token; Retry
 * repeats `reconcile()` only, never the write. A card whose line is terminal stays on
 * screen with its line after the list dropped it.
 */
export function InvitationList({ lang, items, origin, highlight = null, onReconcile, invitations, onBeforeAccept, onDeclined, fallback = null, leave = null }: Props) {
  const copy = MEMBERS_COPY[lang]
  const [busy, setBusy] = useState<string | null>(null)
  const [lines, setLines] = useState<Record<string, string>>({})
  const [kept, setKept] = useState<Invitation[]>([])
  const [joining, setJoining] = useState<Joining | null>(null)
  // An Accept with no reply whose follow-up reads failed: only Retry, which never writes.
  const [uncertain, setUncertain] = useState<Invitation[]>([])
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const line = (invitation: Invitation, text: string | null) => setLines((current) => {
    const next = { ...current }
    if (text === null) delete next[invitation.id]
    else next[invitation.id] = text
    return next
  })
  /**
   * A terminal line: the shared list drops the invitation, so older reads cannot restore it
   * to the counts, and the card stays here with its line.
   */
  const terminal = (invitation: Invitation, text: string) => {
    line(invitation, text)
    setKept((current) => (current.some((i) => i.id === invitation.id) ? current : [...current, invitation]))
    invitations.settle(invitation.id)
  }

  const intentFor = (invitation: Invitation): Intent => ({ kind: 'accepted', companyId: invitation.companyId, origin })
  const doubt = (invitation: Invitation, held: boolean) => setUncertain((current) => {
    const rest = current.filter((i) => i.id !== invitation.id)
    return held ? [...rest, invitation] : rest
  })

  /** The write is known to have happened: read until the company shows, or offer Retry. */
  async function joined(invitation: Invitation) {
    setJoining({ invitation, phase: 'pending' })
    const result = await onReconcile(intentFor(invitation))
    if (result.status === 'ok' && result.outcome === 'applied') {
      invitations.settle(invitation.id)
      if (alive.current) setJoining(null)
      return
    }
    if (!alive.current || result.status === 'unauthenticated') return
    setJoining({ invitation, phase: 'failed' })
  }

  /** The write's outcome is unknown: the memberships decide; `missing` runs `onMissing`. */
  async function confirm(invitation: Invitation, onMissing: () => Promise<void> | void) {
    const result = await onReconcile(intentFor(invitation))
    if (result.status === 'ok' && result.outcome === 'applied') {
      doubt(invitation, false)
      invitations.settle(invitation.id)
      return
    }
    if (!alive.current || result.status === 'unauthenticated') return
    if (result.status === 'ok') {
      await onMissing()
      return
    }
    // The memberships are unknown, so Accept stays off until a Retry reads them.
    doubt(invitation, true)
    line(invitation, copy.notConfirmed)
  }

  /**
   * After an Accept with no reply and no membership: a read that lacks the invitation shows
   * it was used, a read that holds it lets Accept run again, and a failed read keeps Retry.
   */
  const settleUnknown = (invitation: Invitation) => confirm(invitation, async () => {
    const listed = await invitations.refetch()
    if (!alive.current) return
    if (listed === null) {
      doubt(invitation, true)
      line(invitation, copy.notConfirmed)
      return
    }
    doubt(invitation, false)
    if (listed.some((i) => i.id === invitation.id)) line(invitation, copy.notConfirmed)
    else terminal(invitation, copy.invUsed)
  })

  async function recheck(invitation: Invitation) {
    if (busy !== null) return
    setBusy(invitation.id)
    line(invitation, null)
    try {
      await settleUnknown(invitation)
    } finally {
      if (alive.current) setBusy(null)
    }
  }

  async function accept(invitation: Invitation) {
    if (busy !== null || joining !== null) return
    setBusy(invitation.id)
    line(invitation, null)
    onBeforeAccept?.()
    const outcome = await acceptInvitation(invitation.id)
    if (!alive.current) return
    try {
      switch (outcome.status) {
        case 'ok':
          await joined(invitation)
          return
        case 'stale':
        case 'expired':
          return
        case 'unreachable':
          await settleUnknown(invitation)
          return
        case 'notFound':
          await invitations.refetch()
          return
        case 'refused':
          if (outcome.code === 'invite_used' || outcome.code === 'already_member') {
            await confirm(invitation, () => terminal(invitation, copy.invUsed))
            return
          }
          if (outcome.code === 'invite_expired' || outcome.code === 'invite_revoked') {
            terminal(invitation, outcome.code === 'invite_expired' ? copy.invExpired : copy.invRevoked)
            return
          }
          break
      }
      line(invitation, copy.failed)
    } finally {
      if (alive.current) setBusy(null)
    }
  }

  async function decline(invitation: Invitation) {
    if (busy !== null || joining !== null) return
    setBusy(invitation.id)
    line(invitation, null)
    const outcome = await declineInvitation(invitation.id)
    if (!alive.current) return
    setBusy(null)
    switch (outcome.status) {
      case 'ok':
        invitations.settle(invitation.id)
        onDeclined?.()
        return
      case 'stale':
      case 'expired':
        return
      case 'notFound':
        void invitations.refetch()
        return
      case 'refused':
        if (outcome.code === 'invite_expired' || outcome.code === 'invite_revoked' || outcome.code === 'invite_used') {
          const text = outcome.code === 'invite_expired' ? copy.invExpired : outcome.code === 'invite_revoked' ? copy.invRevoked : copy.invUsed
          terminal(invitation, text)
          return
        }
        break
    }
    line(invitation, copy.failed)
  }

  const extra = [...kept, ...uncertain.filter((u) => !kept.some((k) => k.id === u.id))]
  const shown = [...items, ...extra.filter((k) => !items.some((i) => i.id === k.id))]
  const now = Date.now()
  // Parents keep the list mounted while it is empty, so the kept cards and Retry outlive the list.
  if (shown.length === 0 && joining === null) return <>{fallback}</>

  return (
    <Stack gap={12} data-testid="invitation-list">
      {joining?.phase === 'failed' && (
        <AlertBanner
          tone="warn"
          action={<Button variant="default" onClick={() => { void joined(joining.invitation) }}>{copy.retry}</Button>}
        >
          {fmt(copy.joinedNotLoaded, { name: joining.invitation.companyName })}
        </AlertBanner>
      )}
      {shown.map((invitation) => {
        const days = Math.max(1, Math.ceil((Date.parse(invitation.expiresAt) - now) / DAY_MS))
        const settled = kept.some((k) => k.id === invitation.id)
        const text = lines[invitation.id]
        return (
          <Card
            key={invitation.id}
            data-testid="invitation-card"
            data-invitation-id={invitation.id}
            style={invitation.id === highlight ? { border: '2px solid var(--mantine-color-brand-1)' } : undefined}
          >
            <Stack gap={10}>
              <Group gap={8} wrap="wrap">
                <Text fw={600} style={{ overflowWrap: 'anywhere' }}>{invitation.companyName}</Text>
                <Tag>{copy.roles[invitation.role]}</Tag>
              </Group>
              <Group gap="4px 12px" wrap="wrap">
                <Text fz="sm" className="grv-muted" style={{ overflowWrap: 'anywhere' }}>{fmt(copy.invitedBy, { email: invitation.inviterEmail })}</Text>
                <Text fz="sm" className="grv-muted">{fmt(copy.expiresIn, { n: days })}</Text>
              </Group>
              {text !== undefined && <Text fz="sm" role="status">{text}</Text>}
              {settled && invitation.id === highlight && leave}
              {!settled && uncertain.some((u) => u.id === invitation.id) && (
                <Group gap="xs">
                  <Button variant="default" loading={busy === invitation.id} disabled={busy !== null} onClick={() => { void recheck(invitation) }}>{copy.retry}</Button>
                </Group>
              )}
              {!settled && !uncertain.some((u) => u.id === invitation.id) && (
                <Group gap="xs">
                  <Button disabled={busy !== null || joining !== null} onClick={() => { void accept(invitation) }}>{copy.accept}</Button>
                  <Button variant="default" disabled={busy !== null || joining !== null} onClick={() => { void decline(invitation) }}>{copy.decline}</Button>
                </Group>
              )}
            </Stack>
          </Card>
        )
      })}
    </Stack>
  )
}
